/* Altara story mode · keyboard-driven presenter view.
 * Four beats · full-screen · advance with Space / → / click ; back with ← ; jump with 1-4 ; R to replay current.
 * Each beat is the corresponding demo route in an iframe (?embed=1&autoplay=1), so the same animations play here.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";

const GOLD = "#C9A24A";
const NAVY = "#0A1F44";

const STEPS = [
  {
    id: "proposal",
    label: "The proposal arrives",
    stage: "01 · Delivery",
    url: "/wallet/demos/abc-wealth-proposal?embed=1&autoplay=1",
  },
  {
    id: "verify",
    label: "Adviser & institution verification",
    stage: "02 · Trust",
    url: "/wallet/demos/altara-trust-verify?embed=1&autoplay=1",
  },
  {
    id: "understand",
    label: "Contextual education",
    stage: "03 · Understand",
    url: "/wallet/demos/altara-understand?embed=1&autoplay=1",
  },
  {
    id: "check-act",
    label: "Fit check · scoped consent · certificate",
    stage: "04 · Check + Act",
    url: "/wallet/demos/altara-check-act?embed=1&autoplay=1",
  },
];

export default function AltaraStoryMode() {
  const [i, setI] = useState(0);
  const [replayKey, setReplayKey] = useState(0);   // bump to force iframe reload
  const [hintVisible, setHintVisible] = useState(true);
  const containerRef = useRef(null);

  // Fade the hint away after a short time
  useEffect(() => {
    const t = setTimeout(() => setHintVisible(false), 4200);
    return () => clearTimeout(t);
  }, []);

  const go = useCallback((next) => {
    setI((cur) => {
      const clamped = Math.max(0, Math.min(STEPS.length - 1, next));
      if (clamped !== cur) setHintVisible(false);
      return clamped;
    });
  }, []);

  const replay = useCallback(() => {
    setReplayKey((k) => k + 1);
    setHintVisible(false);
  }, []);

  // Keyboard handling
  useEffect(() => {
    const onKey = (e) => {
      // Number keys 1-4 → jump directly
      if (e.key >= "1" && e.key <= String(STEPS.length)) {
        e.preventDefault();
        go(Number(e.key) - 1);
        return;
      }
      switch (e.key) {
        case " ":
        case "ArrowRight":
        case "PageDown":
          e.preventDefault();
          go(i + 1);
          break;
        case "ArrowLeft":
        case "PageUp":
          e.preventDefault();
          go(i - 1);
          break;
        case "Home":
          e.preventDefault();
          go(0);
          break;
        case "End":
          e.preventDefault();
          go(STEPS.length - 1);
          break;
        case "r":
        case "R":
          e.preventDefault();
          replay();
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [i, go, replay]);

  // Click on background advances (ignore clicks on controls)
  const onBackgroundClick = (e) => {
    const target = e.target;
    if (target.closest("[data-story-control]")) return;
    go(i + 1);
  };

  const current = STEPS[i];

  return (
    <div
      ref={containerRef}
      onClick={onBackgroundClick}
      data-testid="altara-story-mode"
      className="fixed inset-0 overflow-hidden select-none cursor-pointer"
      style={{ background: "#050914" }}
    >
      {/* The active beat renders in an iframe filling the viewport */}
      <iframe
        key={`${current.id}-${replayKey}`}
        src={current.url}
        title={current.label}
        className="absolute inset-0 w-full h-full border-0"
        allow="autoplay; fullscreen"
      />

      {/* Top-left · stage marker */}
      <div className="absolute top-6 left-6 pointer-events-none">
        <div className="text-[10px] uppercase tracking-[0.32em] font-semibold"
             style={{ color: GOLD, textShadow: "0 2px 12px rgba(0,0,0,0.8)" }}>
          Altara Consumer Intelligence · Judges Story Mode
        </div>
        <div className="text-white font-semibold mt-1 text-xl"
             style={{ textShadow: "0 2px 16px rgba(0,0,0,0.85)" }}>
          {current.stage} · {current.label}
        </div>
      </div>

      {/* Bottom controls · dots + arrows + replay */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 z-10"
           data-story-control
           onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => go(i - 1)}
          data-testid="story-prev"
          className="w-10 h-10 rounded-full flex items-center justify-center text-white transition"
          style={{ background: "rgba(255,255,255,0.10)", backdropFilter: "blur(6px)" }}
          aria-label="Previous beat"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2 px-4 py-2 rounded-full"
             style={{ background: "rgba(10,20,40,0.55)", backdropFilter: "blur(6px)", border: `1px solid ${GOLD}55` }}>
          {STEPS.map((s, idx) => (
            <button
              key={s.id}
              onClick={() => go(idx)}
              data-testid={`story-dot-${idx}`}
              aria-label={`Go to ${s.label}`}
              className="rounded-full transition-all"
              style={{
                width: idx === i ? 34 : 10,
                height: 10,
                background: idx === i ? GOLD : "rgba(255,255,255,0.35)",
              }}
            />
          ))}
        </div>

        <button
          onClick={replay}
          data-testid="story-replay"
          className="w-10 h-10 rounded-full flex items-center justify-center text-white transition"
          style={{ background: "rgba(255,255,255,0.10)", backdropFilter: "blur(6px)" }}
          aria-label="Replay current beat"
        >
          <RotateCcw className="w-4 h-4" />
        </button>

        <button
          onClick={() => go(i + 1)}
          data-testid="story-next"
          className="w-10 h-10 rounded-full flex items-center justify-center transition"
          style={{ background: GOLD, color: NAVY }}
          aria-label="Next beat"
        >
          <ChevronRight className="w-5 h-5" strokeWidth={2.6} />
        </button>
      </div>

      {/* Bottom-right · beat counter */}
      <div className="absolute bottom-6 right-6 text-white/70 text-[13px] tabular-nums pointer-events-none"
           style={{ textShadow: "0 2px 12px rgba(0,0,0,0.85)" }}>
        <span className="text-white font-semibold">{String(i + 1).padStart(2, "0")}</span>
        <span className="mx-1">/</span>
        <span>{String(STEPS.length).padStart(2, "0")}</span>
      </div>

      {/* Keyboard hint · fades out */}
      <div
        className="absolute top-6 right-6 pointer-events-none transition-opacity duration-1000"
        style={{ opacity: hintVisible ? 1 : 0 }}
      >
        <div className="px-4 py-3 rounded-xl text-[12px] text-white"
             style={{ background: "rgba(10,20,40,0.7)", backdropFilter: "blur(6px)",
                      border: `1px solid ${GOLD}55`, textShadow: "0 1px 4px rgba(0,0,0,0.7)" }}>
          <div className="uppercase tracking-widest text-[10px] mb-1" style={{ color: GOLD }}>
            Presenter controls
          </div>
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            <kbd className="font-mono">Space / →</kbd><span>Next beat</span>
            <kbd className="font-mono">←</kbd><span>Previous beat</span>
            <kbd className="font-mono">1 – 4</kbd><span>Jump to beat</span>
            <kbd className="font-mono">R</kbd><span>Replay current</span>
            <kbd className="font-mono">Click</kbd><span>Next</span>
          </div>
        </div>
      </div>
    </div>
  );
}
